import {
  Box,
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Autocomplete,
  Typography,
  CircularProgress,
  MenuItem,
  useTheme,
} from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { AddCircleOutlineOutlined } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import { tokens } from "../../../theme";
import { ClientInformationProps } from "../../../types/steps";
import { BookingFieldsConfig } from "../../../types/bookings";
import CustomTextField from "../../../components/common/Textfield";
import apiService from "../../../service/Api";
import { toast } from "react-toastify";
import AddBookingFormFields from "../components/AddBookingFormFields";
import { MuiTelInput, MuiTelInputInfo } from "mui-tel-input";
import { IContactFormData } from "../../../types/contact";
import { useNavigate } from "react-router-dom";
import { ArrowRightCircle } from "lucide-react";

interface IBranch {
  id?: string | number;
  company_id: string | number;
  name: string;
  branch_rep: string;
  email: string;
  phone_number: string;
}

const BookingClientInformation: React.FC<ClientInformationProps> = ({
  serviceType,
  bookingData,
  setBookingData,
  currentContact,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, reduxTheme);
  const [companyOptions, setCompanyOptions] = useState<IContactFormData[]>([]);
  const [newCompanyData, setNewCompanyData] = useState<
    Partial<IContactFormData>
  >({
    name: "",
    email: "",
    phone: "",
  });
  const [savingNewCompany, setSavingNewCompany] = useState(false);
  const [branchOptions, setBranchOptions] = useState<IBranch[]>([]);
  const [contactOptions, setContactOptions] = useState<IContactFormData[]>([]);
  const [newBranch, setNewBranch] = useState<IBranch>({
    company_id: "",
    name: "",
    branch_rep: "",
    email: "",
    phone_number: "",
  });
  const [openAddBranchDialog, setOpenAddBranchDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [branchLoading, setBranchLoading] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [openAddContactDialog, setOpenAddContactDialog] = useState(false);
  const [openAddCompanyDialog, setOpenAddCompanyDialog] = useState(false);
  const [newContactData, setNewContactData] = useState<
    Partial<IContactFormData>
  >({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });
  const [savingNewContact, setSavingNewContact] = useState(false);
  const [contactSearchTerm, setContactSearchTerm] = useState("");
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [referralPartnerOptions, setReferralPartnerOptions] = useState<
    { id: number; name: string; surname: string }[]
  >([]);
  const [referralPartnerLoading, setReferralPartnerLoading] = useState(false);
  const [referralSearchTerm, setReferralSearchTerm] = useState("");
  const navigate = useNavigate();
  // Fetch branches when company_id changes
  useEffect(() => {
    const fetchBranches = async () => {
      if (!bookingData.company_id) {
        setBranchOptions([]);
        return;
      }
      setBranchLoading(true);
      try {
        const branchResponse = await apiService.get<{ results: IBranch[] }>(
          `/api/companies/company-branch/?company_id=${bookingData.company_id}`,
        );
        setBranchOptions(branchResponse.data.results || []);
      } catch (err: unknown) {
        console.error("Failed to fetch branches:", err);
        toast.error("Failed to load branches. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
      } finally {
        setBranchLoading(false);
      }
    };

    fetchBranches();
  }, [bookingData.company_id]);

  // Debounced fetch for contacts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (contactSearchTerm.length >= 3) {
        fetchContacts(contactSearchTerm);
      } else {
        setContactOptions([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [contactSearchTerm]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (referralSearchTerm.length >= 3) {
        fetchReferralPartners(referralSearchTerm);
      } else {
        setReferralPartnerOptions([]);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [referralSearchTerm]);
  // Debounced fetch for companies
  useEffect(() => {
    const timer = setTimeout(() => {
      if (companySearchTerm.length >= 3) {
        fetchCompanies(companySearchTerm);
      } else {
        setCompanyOptions([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [companySearchTerm]);

  const fetchContacts = async (search: string) => {
    setContactLoading(true);
    try {
      const contactResponse = await apiService.get<{
        results: IContactFormData[];
      }>(`/api/contacts/contact/?search=${search}`);
      setContactOptions(contactResponse.data.results || []);
    } catch (err: unknown) {
      console.error("Failed to fetch contacts:", err);
      toast.error("Failed to load contacts. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setContactLoading(false);
    }
  };

  const fetchCompanies = async (search: string) => {
    setCompanyLoading(true);
    try {
      const companyResponse = await apiService.get<{
        results: IContactFormData[];
      }>(`/api/contacts/contact/?search=${search}&contact_type=company`);
      setCompanyOptions(companyResponse.data.results || []);
    } catch (err: unknown) {
      console.error("Failed to fetch companies:", err);
      toast.error("Failed to load companies. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setCompanyLoading(false);
    }
  };

  const fetchReferralPartners = async (search: string) => {
    setReferralPartnerLoading(true);
    try {
      const response = await apiService.get<{
        results: { id: number; name: string; surname: string }[];
      }>(`/api/refferal-program/refferal-programs/?search=${search}`);
      setReferralPartnerOptions(response.data.results || []);
    } catch (err) {
      console.error("Failed to fetch referral partners:", err);
      toast.error("Failed to load referral partners. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setReferralPartnerLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }
    >,
  ) => {
    const { name, value } = e.target;
    if (name) {
      setBookingData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleContactChange = (
    event: React.SyntheticEvent,
    value: IContactFormData | null,
  ) => {
    setBookingData((prev) => ({
      ...prev,
      contact_id: Number(value?.id),
      first_name: value?.first_name || "",
      surname: value?.last_name || "",
      email: value?.email || "",
      phone: value?.phone || "",
    }));
  };

  const handleBranchChange = (
    event: React.SyntheticEvent,
    value: IBranch | null,
  ) => {
    setBookingData((prev) => ({
      ...prev,
      branch_name: value?.name || "",
      branch_rep: value?.branch_rep || "",
      representative_email: value?.email || "",
      representative_phone: value?.phone_number || "",
    }));
  };

  const handleOpenAddBranchDialog = () => {
    setNewBranch({
      company_id: bookingData.company_id || "",
      name: "",
      branch_rep: "",
      email: "",
      phone_number: "",
    });
    setOpenAddBranchDialog(true);
    setErrorMessage("");
  };

  const handleCloseAddBranchDialog = () => {
    setOpenAddBranchDialog(false);
    setNewBranch({
      company_id: "",
      name: "",
      branch_rep: "",
      email: "",
      phone_number: "",
    });
    setErrorMessage("");
  };

  //Add Branch to a company
  const handleAddBranch = async () => {
    setLoading(true);
    try {
      const response = await apiService.post<IBranch>(
        "/api/companies/company-branch/",
        {
          company_id: newBranch.company_id,
          name: newBranch.name,
          branch_rep: newBranch.branch_rep,
          email: newBranch.email,
          phone: newBranch.phone_number,
        },
      );
      setBranchOptions((prev) => [...prev, response.data]);
      setBookingData((prev) => ({
        ...prev,
        branch_name: response.data.name,
        branch_rep: response.data.branch_rep,
        representative_email: response.data.email,
        representative_phone: response.data.phone_number,
      }));
      toast.success("Branch added successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      handleCloseAddBranchDialog();
    } catch (err: unknown) {
      console.error("Failed to add branch:", err);
      const errorMessage =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Failed to add branch. Please try again.";
      setErrorMessage(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setLoading(false);
    }
  };

  const clientFields =
    (AddBookingFormFields as BookingFieldsConfig)[serviceType]?.clientFields ||
    [];

  const handleOpenAddContactDialog = () => {
    setOpenAddContactDialog(true);
    setNewContactData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
    });
  };
  const handleCloseAddContactDialog = () => {
    setOpenAddContactDialog(false);
  };

  const handleCloseAddCompanyDialog = () => {
    setOpenAddCompanyDialog(false);
  };

  const handleNewContactChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setNewContactData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneInputChange = (value: string, _info: MuiTelInputInfo) => {
    void _info;
    setNewContactData((prev) => ({
      ...prev,
      phone: value,
    }));
  };

  const handlePhoneBranchInputChange = (
    value: string,
    _info: MuiTelInputInfo,
  ) => {
    void _info;
    setNewBranch((prev) => ({
      ...prev,
      phone_number: value,
    }));
  };
  const handleNewCompanyChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setNewCompanyData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  //Save a new contact
  const handleSaveNewContact = async () => {
    setSavingNewContact(true);
    try {
      const res = await apiService.post<IContactFormData>(
        "/api/contacts/contact/",
        {
          ...newContactData,
          contact_type: "individual",
        },
      );
      const savedContact = res.data;

      toast.success("Contact added successfully!", {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
      });

      setContactOptions((prevOptions) => [...prevOptions, savedContact]);

      setBookingData((prevBookingData) => ({
        ...prevBookingData,
        contact_id: Number(savedContact.id),
        first_name: savedContact.first_name || "",
        surname: savedContact.last_name || "",
        email: savedContact.email || "",
        phone: savedContact.phone || "",
      }));

      handleCloseAddContactDialog();
    } catch (error: unknown) {
      console.error("Failed to save new contact:", error);
      let errorMessage = "Failed to add contact. Please try again.";
      const errorData = (error as { response?: { data?: unknown } })?.response
        ?.data;
      if (errorData && typeof errorData === "object") {
        errorMessage =
          Object.values(errorData).flat().join(" ") || errorMessage;
      }
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setSavingNewContact(false);
    }
  };
  //save new company
  const handleSaveNewCompany = async () => {
    setSavingNewCompany(true);
    try {
      const res = await apiService.post<IContactFormData>(
        "/api/contacts/contact/",
        newCompanyData,
      );
      const savedCompany = res.data;

      toast.success("Company added successfully!", {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
      });

      setCompanyOptions((prevOptions) => [...prevOptions, savedCompany]);

      setBookingData((prevBookingData) => ({
        ...prevBookingData,
        company_contact_id: savedCompany.id,
        company_name: savedCompany.name || "",
        email: savedCompany.email || "",
        phone: savedCompany.phone || "",
      }));

      handleCloseAddCompanyDialog();
    } catch (error: unknown) {
      console.error("Failed to save new company:", error);
      let errorMessage = "Failed to add company. Please try again.";
      const errorData = (error as { response?: { data?: unknown } })?.response
        ?.data;
      if (errorData && typeof errorData === "object") {
        errorMessage =
          Object.values(errorData).flat().join(" ") || errorMessage;
      }
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setSavingNewCompany(false);
    }
  };

  const handlePhoneCompanyInputChange = (
    value: string,
    _info: MuiTelInputInfo,
  ) => {
    void _info;
    setNewCompanyData((prev) => ({
      ...prev,
      phone: value,
    }));
  };

  const allContactOptions = useMemo(() => {
    const options = [...contactOptions];

    if (currentContact && !options.find((c) => c.id === currentContact.id)) {
      options.unshift(currentContact);
    }

    return options;
  }, [contactOptions, currentContact]);

  const selectedContact: IContactFormData | null =
    allContactOptions.find((c) => c.id === bookingData.contact_id) ||
    (bookingData.contact_id
      ? {
          id: bookingData.contact_id,
          first_name: bookingData.first_name ?? "",
          last_name: bookingData.surname ?? "",
          email: bookingData.email ?? "",
          phone: bookingData.phone ?? "",
        }
      : null);

  return (
    <Box p={2}>
      <Grid container spacing={2}>
        {clientFields.map((field) => (
          <React.Fragment key={field.name}>
            <Grid size={{ xs: 12, md: 4 }} key={field.name}>
              {(serviceType === "FUNERAL_HOME" || serviceType === "EVENT") &&
              (field.name === "first_name" ||
                field.name === "company_name" ||
                field.name === "branch_name") ? (
                <>
                  {field.name === "company_name" && (
                    <div>
                      <Autocomplete
                        fullWidth
                        options={companyOptions}
                        getOptionLabel={(option: IContactFormData) =>
                          option.name || ""
                        }
                        value={
                          companyOptions.find(
                            (c) => c.id === bookingData.company_id,
                          ) || null
                        }
                        onChange={(
                          event,
                          newValue: IContactFormData | null,
                        ) => {
                          setBookingData((prev) => ({
                            ...prev,
                            company_id: newValue ? newValue.id : null,
                            company_name: newValue ? newValue.name : "",
                            email: newValue ? newValue.email || "" : "",
                            phone: newValue ? newValue.phone || "" : "",
                          }));
                        }}
                        onInputChange={(event, newInputValue) => {
                          setCompanySearchTerm(newInputValue);
                        }}
                        renderInput={(params) => (
                          <CustomTextField
                            {...params}
                            label="Company Contact"
                            variant="outlined"
                            helperText="Type at least 3 characters to search companies"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {companyLoading && (
                                    <CircularProgress
                                      size={20}
                                      sx={{
                                        color:
                                          colors.primary[400],
                                      }}
                                    />
                                  )}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                "& fieldset": {
                                  borderColor:
                                    colors.primary[300],
                                },
                                "&:hover fieldset": {
                                  borderColor:
                                    colors.neutral[400],
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor:
                                    colors.primary[400],
                                },
                              },
                              "& .MuiInputLabel-root": {
                                color:
                                  colors.primary[300],
                              },
                              "& .MuiInputBase-input": {
                                color:
                                  colors.neutral[100],
                              },
                            }}
                          />
                        )}
                      />
                      <Button
                        startIcon={<ArrowRightCircle />}
                        onClick={() => {
                          navigate("/contacts/add-company-contact");
                        }}
                        sx={{
                          mt: 1,

                          color: colors.primary[400],

                          "&:hover": {
                            backgroundColor:
                              colors.neutral[800],
                          },
                        }}
                      >
                        Add Company Contact
                      </Button>
                    </div>
                  )}
                  {field.name === "branch_name" && (
                    <div>
                      <Autocomplete
                        fullWidth
                        options={branchOptions}
                        getOptionLabel={(option: IBranch) => option.name || ""}
                        value={
                          branchOptions.find(
                            (b) => b.name === bookingData.branch_name,
                          ) || null
                        }
                        onChange={handleBranchChange}
                        disabled={!bookingData.company_id}
                        renderInput={(params) => (
                          <CustomTextField
                            {...params}
                            label="Branch"
                            variant="outlined"
                            InputProps={{
                              ...params.InputProps,

                              endAdornment: (
                                <>
                                  {branchLoading && (
                                    <CircularProgress
                                      size={20}
                                      sx={{
                                        color:
                                          colors.primary[400],
                                      }}
                                    />
                                  )}

                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />

                      <Button
                        startIcon={<AddCircleOutlineOutlined />}
                        onClick={handleOpenAddBranchDialog}
                        disabled={!bookingData.company_id}
                        sx={{
                          mt: 1,

                          color: colors.primary[400],

                          "&:hover": {
                            backgroundColor:
                              colors.neutral[800],
                          },
                        }}
                      >
                        Add Branch
                      </Button>
                    </div>
                  )}
                </>
              ) : field.name === "first_name" ? (
                <div>
                  <Autocomplete
                    fullWidth
                    options={allContactOptions}
                    getOptionLabel={(option: IContactFormData) =>
                      `${option.first_name || ""} ${
                        option.last_name || ""
                      }`.trim()
                    }
                    // value={
                    //   contactOptions.find(
                    //     (c) => c.id === bookingData.contact_id
                    //   ) || null
                    // }
                    value={selectedContact}
                    onChange={(event, newValue: IContactFormData | null) => {
                      handleContactChange(event, newValue);
                      setBookingData((prevBookingData) => ({
                        ...prevBookingData,
                        contact_id: newValue ? newValue.id : null,
                        first_name: newValue ? newValue.first_name : "",
                        surname: newValue ? newValue.last_name : "",
                        email: newValue ? newValue.email || "" : "",
                        phone: newValue ? newValue.phone || "" : "",
                      }));
                    }}
                    onInputChange={(event, newInputValue) => {
                      setContactSearchTerm(newInputValue);
                    }}
                    loading={contactLoading}
                    renderInput={(params) => (
                      <CustomTextField
                        {...params}
                        label="Client Name"
                        variant="outlined"
                        helperText="Type at least 3 characters to search contacts"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {contactLoading && (
                                <CircularProgress
                                  size={20}
                                  sx={{
                                    color:
                                      colors.primary[400],
                                  }}
                                />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            "& fieldset": {
                              borderColor:
                                colors.primary[300],
                            },
                            "&:hover fieldset": {
                              borderColor:
                                colors.neutral[400],
                            },
                            "&.Mui-focused fieldset": {
                              borderColor:
                                colors.primary[800],
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: colors.neutral[400],
                          },
                          "& .MuiInputBase-input": {
                            color: colors.neutral[100],
                          },
                        }}
                      />
                    )}
                  />

                  <Button
                    startIcon={<AddCircleOutlineOutlined />}
                    onClick={handleOpenAddContactDialog}
                    sx={{
                      mt: 1,

                      color: colors.primary[400],

                      "&:hover": {
                        backgroundColor:
                          colors.neutral[800],
                      },
                    }}
                  >
                    Add Contact
                  </Button>
                </div>
              ) : (
                <CustomTextField
                  fullWidth
                  select={field.type === "select"}
                  type={field.type === "date" ? "date" : "text"}
                  label={field.label}
                  name={field.name}
                  disabled={field.disabled}
                  value={bookingData[field.name] || ""}
                  onChange={handleChange}
                  variant="outlined"
                  InputLabelProps={
                    field.type === "date" ? { shrink: true } : undefined
                  }
                >
                  {field.type === "select" && (
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                  )}
                  {field.type === "select" &&
                    field.options?.map((option, idx) => {
                      const optionValue =
                        typeof option === "object"
                          ? (option as { value: string }).value
                          : option;
                      const optionLabel =
                        typeof option === "object"
                          ? (option as { label: string }).label
                          : option;

                      return (
                        <MenuItem
                          key={`${field.name}-${optionValue}-${idx}`}
                          value={optionValue}
                        >
                          {optionLabel}
                        </MenuItem>
                      );
                    })}
                </CustomTextField>
              )}
            </Grid>
            {field.name === "source_of_booking" &&
              bookingData.source_of_booking?.toLowerCase() === "referral" && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <Autocomplete
                    fullWidth
                    options={referralPartnerOptions}
                    getOptionLabel={(option) =>
                      `${option.name} ${option.surname}`.trim()
                    }
                    value={
                      referralPartnerOptions.find(
                        (p) => p.id === bookingData.referral_partner_id,
                      ) || null
                    }
                    onChange={(_event, newValue) => {
                      setBookingData((prev) => ({
                        ...prev,
                        referral_partner_id: newValue ? newValue.id : null,
                        referral_partner_name: newValue
                          ? `${newValue.name} ${newValue.surname}`.trim()
                          : null,
                      }));
                    }}
                    onInputChange={(_event, newInputValue) => {
                      setReferralSearchTerm(newInputValue);
                    }}
                    loading={referralPartnerLoading}
                    renderInput={(params) => (
                      <CustomTextField
                        {...params}
                        label="Referred By"
                        placeholder="Referred By"
                        variant="outlined"
                        helperText="Type at least 3 characters to search"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {referralPartnerLoading && (
                                <CircularProgress
                                  size={20}
                                  sx={{
                                    color:
                                      colors.primary[400],
                                  }}
                                />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            "& fieldset": {
                              borderColor:
                                colors.primary[300],
                            },
                            "&:hover fieldset": {
                              borderColor:
                                colors.neutral[400],
                            },
                            "&.Mui-focused fieldset": {
                              borderColor:
                                colors.primary[400],
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: colors.primary[300],
                          },
                          "& .MuiInputBase-input": {
                            color: colors.neutral[100],
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
          </React.Fragment>
        ))}
      </Grid>

      <Dialog
        open={openAddBranchDialog}
        onClose={handleCloseAddBranchDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.neutral[900],
            color: colors.neutral[100],
          }}
        >
          Add New Branch
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: colors.neutral[900],
          }}
        >
          {errorMessage && (
            <Typography color="error" mb={2}>
              {errorMessage}
            </Typography>
          )}
          <Autocomplete
            fullWidth
            options={companyOptions}
            getOptionLabel={(option: IContactFormData) => option.name || ""}
            value={
              companyOptions.find((c) => c.id === newBranch.company_id) || null
            }
            onChange={(event, value: IContactFormData | null) =>
              setNewBranch((prev) => ({
                ...prev,
                company_id: value?.id || "",
              }))
            }
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label="Company"
                variant="outlined"
                margin="normal"
                required
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: colors.primary[300],
                    },
                    "&:hover fieldset": {
                      borderColor: colors.neutral[400],
                    },
                    "&.Mui-focused fieldset": {
                      borderColor:
                        colors.primary[400],
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: colors.primary[300],
                  },
                  "& .MuiInputBase-input": {
                    color: colors.neutral[100],
                  },
                }}
              />
            )}
          />
          <CustomTextField
            fullWidth
            label="Branch Name"
            name="name"
            value={newBranch.name}
            onChange={(e) =>
              setNewBranch((prev) => ({ ...prev, name: e.target.value }))
            }
            variant="outlined"
            margin="normal"
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: colors.primary[300],
                },
                "&:hover fieldset": {
                  borderColor: colors.neutral[400],
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary[400],
                },
              },
              "& .MuiInputLabel-root": {
                color: colors.primary[300],
              },
              "& .MuiInputBase-input": {
                color: colors.neutral[100],
              },
            }}
          />
          <CustomTextField
            fullWidth
            label="Branch Representative"
            name="branch_rep"
            value={newBranch.branch_rep}
            onChange={(e) =>
              setNewBranch((prev) => ({ ...prev, branch_rep: e.target.value }))
            }
            variant="outlined"
            margin="normal"
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: colors.primary[300],
                },
                "&:hover fieldset": {
                  borderColor: colors.neutral[400],
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary[400],
                },
              },
              "& .MuiInputLabel-root": {
                color: colors.primary[300],
              },
              "& .MuiInputBase-input": {
                color: colors.neutral[100],
              },
            }}
          />
          <CustomTextField
            fullWidth
            label="Email"
            name="email"
            value={newBranch.email}
            onChange={(e) =>
              setNewBranch((prev) => ({ ...prev, email: e.target.value }))
            }
            variant="outlined"
            margin="normal"
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: colors.primary[300],
                },
                "&:hover fieldset": {
                  borderColor: colors.neutral[400],
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary[400],
                },
              },
              "& .MuiInputLabel-root": {
                color: colors.primary[300],
              },
              "& .MuiInputBase-input": {
                color: colors.neutral[100],
              },
            }}
          />

          <MuiTelInput
            margin="dense"
            name="phone"
            label="Phone Number"
            fullWidth
            variant="outlined"
            value={newBranch.phone_number || ""}
            defaultCountry="ZW"
            onChange={handlePhoneBranchInputChange}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: colors.neutral[900],
          }}
        >
          <Button
            onClick={handleCloseAddBranchDialog}
            sx={{
              color: colors.primary[100],
              borderColor: colors.primary[100],
              backgroundColor: colors.primary[800],
              "&:hover": {
                borderColor: colors.neutral[400],
                backgroundColor: colors.neutral[800],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddBranch}
            sx={{
              backgroundColor: colors.primary[400],
              color: colors.primary[900],
              "&:hover": {
                backgroundColor: colors.primary[500],
              },
              "&.Mui-disabled": {
                backgroundColor: colors.neutral[400],
                color: colors.primary[900],
              },
            }}
            variant="contained"
            disabled={
              loading ||
              !newBranch.company_id ||
              !newBranch.name ||
              !newBranch.branch_rep ||
              !newBranch.email ||
              !newBranch.phone_number
            }
          >
            {loading ? (
              <CircularProgress
                size={20}
                sx={{ color: colors.neutral[100] }}
              />
            ) : (
              "Add Branch"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openAddContactDialog} onClose={handleCloseAddContactDialog}>
        <DialogTitle>Add New Contact</DialogTitle>
        <DialogContent>
          <CustomTextField
            autoFocus
            margin="dense"
            name="first_name"
            label="First Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newContactData.first_name}
            onChange={handleNewContactChange}
            sx={{ mb: 2 }}
          />
          <CustomTextField
            margin="dense"
            name="last_name" // Assuming your backend expects last_name
            label="Surname"
            type="text"
            fullWidth
            variant="outlined"
            value={newContactData.last_name}
            onChange={handleNewContactChange}
            sx={{ mb: 2 }}
          />
          <CustomTextField
            margin="dense"
            name="email"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={newContactData.email}
            onChange={handleNewContactChange}
            sx={{ mb: 2 }}
          />
          <MuiTelInput
            margin="dense"
            name="phone"
            label="Phone Number"
            fullWidth
            variant="outlined"
            value={newContactData.phone || ""}
            defaultCountry="ZW"
            onChange={handlePhoneInputChange}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddContactDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewContact}
            color="primary"
            disabled={savingNewContact}
          >
            {savingNewContact ? <CircularProgress size={24} /> : "Save Contact"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openAddCompanyDialog} onClose={handleCloseAddCompanyDialog}>
        <DialogTitle>Add New Company</DialogTitle>
        <DialogContent>
          <CustomTextField
            autoFocus
            margin="dense"
            name="name"
            label="Company Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newCompanyData.name}
            onChange={handleNewCompanyChange}
            sx={{ mb: 2 }}
          />
          <CustomTextField
            margin="dense"
            name="email"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={newCompanyData.email}
            onChange={handleNewCompanyChange}
            sx={{ mb: 2 }}
          />
          <MuiTelInput
            margin="dense"
            name="phone"
            label="Phone Number"
            fullWidth
            variant="outlined"
            value={newCompanyData.phone || ""}
            defaultCountry="ZW"
            onChange={handlePhoneCompanyInputChange}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddCompanyDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewCompany}
            color="primary"
            disabled={savingNewCompany}
          >
            {savingNewCompany ? <CircularProgress size={24} /> : "Save Company"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

BookingClientInformation.propTypes = {
  serviceType: PropTypes.string.isRequired,
  bookingData: PropTypes.shape({
    company_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    contact_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    first_name: PropTypes.string,
    surname: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    branch_name: PropTypes.string,
    branch_rep: PropTypes.string,
    representative_email: PropTypes.string,
    representative_phone: PropTypes.string,
    special_request: PropTypes.string,
    service_notes: PropTypes.string,
  }).isRequired,
  setBookingData: PropTypes.func.isRequired,
  currentContact: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
  }),
};

export default BookingClientInformation;
